import { useEffect, useState } from "react";
import { useOrganization } from "../../../../biz/hooks/entities/useOrganization";
import { useFetch } from "../../../../biz/hooks/data/useFetcher";
import { RdxType, RestItemContainer, RestListContainer, RestResponseType, StringObjectDictionary, StringStringDictionary } from "../../../../biz/data/Types";
import { AdItemType, AdItemTypeType, AdProjectResponseType, AdProjectType, AdStateType, CHANGE_ORGANIZATION_PROJECTS } from "../../../../biz/data/redux/organizationReducer";
import { useDispatch, useSelector } from "react-redux";
import { CHANGE_UI_ADS_UI_SETTINGS, PlantSearchUIType } from "../../../../biz/data/redux/uIReducer";
import { Config } from "../../../../biz/data/Config";
import { useDateFunctions } from "../../../../biz/translations/useFormatting";
import { useTranslation } from "react-i18next";

// **************************** Types ****************************
type AdSettingType = {
    type: AdItemTypeType,
    description: string,
    title01Label?: string,
    title02Label?: string,
    title03Label?: string,
    title04Label?: string,
    title01Length?: number,
    title02Length?: number,
    title03Length?: number,
    title04Length?: number,
    title01Multiline?: boolean,
    title02Multiline?: boolean,
    title03Multiline?: boolean,
    title04Multiline?: boolean,
    hasPriceSettings?: boolean
}


export const useAdsSettings = (): AdSettingType[] => {
    const { t } = useTranslation();
    return [
        {
            type: "discovery_featured",
            description: t("ad_desc_discovery_featured"),
            title01Label: t("ad_label_header"),
            title02Label: t("ad_label_description"),
            title02Length: 100,
            title02Multiline: true,
            title03Label: t("ad_label_highlight_1"),
            title04Label: t("ad_label_highlight_2"),
            hasPriceSettings: true
        },
        {
            type: "discovery_featured_secondary",
            description: t("ad_desc_discovery_featured_secondary"),
            title01Label: t("ad_label_header"),
            title02Label: t("ad_label_description"),
            title02Length: 100,
            title02Multiline: true,
            title03Label: t("ad_label_highlight"),
            title04Label: t("ad_label_highlight_2"),
            hasPriceSettings: true
        },
        {
            type: "calendar_list",
            description: t("ad_desc_calendar_list"),
            title01Label: t("ad_label_header"),
            title02Label: t("ad_label_description"),
            title02Length: 100,
            title02Multiline: true,
            title03Label: t("ad_label_highlight_1"),
            title04Label: t("ad_label_highlight_2"),
            hasPriceSettings: true
        },
        {
            type: "monthly_article",
            description: t("ad_desc_monthly_article"),
            title01Label: t("ad_label_header"),
            title02Label: t("ad_label_description"),
            title02Length: 100,
            title02Multiline: true,
        },
        {
            type: "plantdetail_main",
            description: t("ad_desc_plantdetail_main"),
            title01Label: t("ad_label_header"),
            title02Label: t("ad_label_description"),
            title02Length: 100,
            title02Multiline: true,
        },
        {
            type: "forum_feed",
            description: t("ad_desc_forum_feed"),
            title01Label: t("ad_label_header"),
            title02Label: t("ad_label_description"),
            title02Length: 100,
            title02Multiline: true,
            title03Label: t("ad_label_highlight_1"),
            title04Label: t("ad_label_highlight_2"),
            hasPriceSettings: true
        },
        {
            type: "plantdetail_main_web",
            description: t("ad_desc_plantdetail_main_web"),
            title01Label: t("ad_label_header"),
            title02Label: t("ad_label_description"),
            title02Length: 100,
            title02Multiline: true,
        },
        {
            type: "plantsearch",
            description: t("ad_desc_plantsearch"),
            title01Label: t("ad_label_header"),
            title02Label: t("ad_label_description"),
            title02Length: 100,
            title02Multiline: true,
            title03Label: t("ad_label_highlight_1"),
            title04Label: t("ad_label_highlight_2"),
            hasPriceSettings: true,
        },
        {
            type: "offer",
            description: t("ad_desc_offer"),
            title01Label: t("ad_offer_header"),
            title02Label: t("ad_offer_description"),
            title02Length: 100,
            title02Multiline: true,
            title03Label: t("ad_label_highlight"),
            hasPriceSettings: true,
        },
    ]
}
// **************************** Data ****************************

// **************************** UI hooks ****************************
export const useAdsUI = () => {

    const dispatch = useDispatch();
    const org = useOrganization();
    const ui = useAdsData(org?.currentOrganization?.id ?? "");
    useEffect(() => {
        if (!!org?.currentOrganization?.id) {
            ui.load();
        }
    }, [, org?.currentOrganization?.id]);

    const adsUISettingsHook = useAdsUISettings();
    const uiSettings = adsUISettingsHook.adsUISettings;

    return {
        allAds: ui.ads(),
        adsInStateDrafting: ui.ads()?.filter(m => m?.adProjectUIState === "drafting") ?? [],
        //adsInStateUpComing: ui.ads?.filter(m => m?.adProjectUIState === "upcoming") ?? [],
        adsInStateActive: ui.ads()?.filter(m => m?.adProjectUIState === "active" || m?.adProjectUIState === "upcoming") ?? [],
        adsInStateFinished: ui.ads()?.filter(m => m?.adProjectUIState === "finished") ?? [],

        isWorking: ui.isWorking,
        reload: ui.load,
        update: ui.update,
        changeState: ui.changeState,
        // 
        adsUISettings: uiSettings,
        selectedTab: uiSettings?.selectedTab,
        updateUI: {
            updateAdsUISettings: adsUISettingsHook.updateAdsUISettings,
            setSelectedTab: adsUISettingsHook.updateAdsUISettingsSelectedTab,
        }
    }
}

export const useAdsUISettings = () => {

    const dispatch = useDispatch();
    const adsUISettings = useSelector((state: RdxType) => state.ui.adsUISettings);
    const plantSearchUI = useSelector((state: RdxType) => state.ui.plantSearchUI);

    const updateAdsUISettings = (e: AdsUISettings) => {
        const p = { ...adsUISettings, ...e };
        dispatch({ type: CHANGE_UI_ADS_UI_SETTINGS, payload: p });
        if (Config.isDev) {
            // console.log("payload:", p);
        }
    };

    const asd = () => {
        const payload: PlantSearchUIType = { ...plantSearchUI, planttarget: { type: "ad", id: "id" } }
    }
    return {

        // Ads
        adsUISettings: adsUISettings,
        updateAdsUISettings: updateAdsUISettings,
        updateAdsUISettingsSelectedTab: (e: AdPackageTopSelectorType) => {
            updateAdsUISettings({ selectedTab: e })
        },

        updateAdsUISettingsEditingId: (id: string, editing: boolean) => {
            let c = { ...(adsUISettings?.editingIds ?? {}) };
            if (!!c?.[id] && editing === false) {
                delete c[id];
                updateAdsUISettings({ editingIds: c })
            } else if (editing === true) {
                c[id] = id;
                updateAdsUISettings({ editingIds: c })
            }


        },
        updateAdsUISettingsProcessing: (id: string, processing: boolean) => {
            let c = { ...(adsUISettings?.processing ?? {}) };
            if (processing === false) {
                delete c[id];
                updateAdsUISettings({ processing: c })
            } else if (processing === true) {
                c[id] = id;
                updateAdsUISettings({ processing: c })
            }
        },
        updateAdPlantIds: (adid: string, e: string[]) => {
            let change = { ...adsUISettings };
            let plantIds = e.join(";").replaceAll(";;", ";");
            plantIds = plantIds.endsWith(";") ? plantIds.substring(0, plantIds.length - 1) : plantIds

            console.log("plantIds: ", plantIds)
            if (!!change.editModels) {
                change.editModels = {
                    ...change?.editModels,
                    [adid]: {
                        ...change.editModels[adid],
                        plantIds: plantIds
                    }
                };
                updateAdsUISettings({ ...change });
            }
        }
    }
}


export type AdsUISettings = {
    editModels?: StringObjectDictionary<AdItemType>,
    selectedTab?: undefined | AdPackageTopSelectorType,
    editingIds?: StringStringDictionary,
    processing?: StringStringDictionary,
    addingPlantsToAdId?: string
}
export type AdPackageTopSelectorType = "drafting" | "upcoming" | "active" | "finished";

// **************************** Data ****************************
const useAdsData = (orgId: string) => {
    const df = useDateFunctions();
    // 
    const dispatch = useDispatch();
    const api = useAdsApi();
    const myAdProjects = useSelector((state: RdxType) => state.organization.myAdProjects);

    const [isWorking, setIsWorking] = useState<boolean>(false);

    // handlers
    const load = async () => {
        //const data: RestListContainer<AdItemType> = await api.get(orgId);
        const data = await api.getProjects(orgId);

        let _change_projects: { [x: string]: AdProjectType } = {};

        data?.item?.adProjects?.forEach(a => {
            _change_projects[a?.id] = a;
            _change_projects[a?.id].adLines = data?.item?.ads?.filter(m => m?.projectId == a.id);

            // if (Config.isDev) {
            //     _change_projects[a.id].startdatekey = 20200515;
            //     _change_projects[a.id].enddatekey = 20200530;
            //     _change_projects[a.id].adstate = "active";
            // }

            const datekey = df.getDateKey();
            if (_change_projects[a.id].adstate === "draft") {
                _change_projects[a.id].adProjectUIState = "drafting";
            } else {
                if (_change_projects[a.id].startdatekey > datekey) {
                    _change_projects[a.id].adProjectUIState = "upcoming";
                } else if (_change_projects[a.id].enddatekey < datekey) {
                    _change_projects[a.id].adProjectUIState = "finished";
                } else {
                    _change_projects[a.id].adProjectUIState = "active";
                }
            }
            // console.log("_change_projects[a.id].adProjectUIState:", _change_projects[a.id].adProjectUIState, datekey)

        });
        dispatch({ type: CHANGE_ORGANIZATION_PROJECTS, payload: _change_projects });

    }

    const update = async (orgId: string, adId: string, model: AdItemType, file: File | undefined) => {
        setIsWorking(true);

        // TODO upload ad Image
        console.log("TODO! Handle file upload(file):", file);

        let formData: FormData = new FormData();
        formData.append('file', file as File);
        formData.append('json', JSON.stringify(model));
        formData.forEach(function (value: any, key: string) {
            console.log("formData:", key, JSON.stringify(value));
        });

        // Update Info
        const res = await api.update(orgId, adId, formData);

        setIsWorking(false);
        return res;
    }

    const changeState = async (orgId: string, projectId: string, state: AdStateType) => {
        setIsWorking(true);
        const res = await api.changeState(orgId, projectId, state);
        setIsWorking(false);
        return res;
    }

    return {
        isWorking: isWorking,
        load: load,
        update: update,
        changeState: changeState,
        ads: () => {
            var l = Object.keys(myAdProjects ?? {}).map(key => myAdProjects?.[key]);
            l.sort((a, b) => {
                if (a?.startdatekey > b?.startdatekey) { return -1; }
                else if (a?.startdatekey < b?.startdatekey) { return 1; }
                else {
                    return 0
                }
            });
            return l;
        },
    }
}

// **************************** Api ****************************
export const useAdsApi = () => {

    const f = useFetch();

    return {
        // get: async (id: string): Promise<RestListContainer<AdItemType>> => {
        //     return await f.get("/api/organization/" + id + "/ads");
        // },
        getProjects: async (id: string): Promise<RestItemContainer<AdProjectResponseType>> => {
            return await f.get("/api/organization/" + id + "/adprojects");
        },
        update: async (orgId: string, adId: string, model: FormData): Promise<RestResponseType> => {
            const url = "/api/organization/" + orgId + "/ads/" + adId;
            return await f.postForm(url, model);

        },
        changeState: async (orgId: string, projectId: string, state: AdStateType): Promise<RestResponseType> => {
            const url = "/api/organization/" + orgId + "/ads/" + projectId + "/state";
            return await f.patch(url, { state: state });
        },
    }
}

